// Resolución de credenciales de comercio Transbank (codeComerce / codeStore /
// apiKey) — histórico ADR-0007. Módulo puro (sin React/Zustand/Sentry, sin
// leer `import.meta.env` internamente — el env se inyecta por parámetro),
// mismo patrón y misma prohibición de mockearlo en tests de componentes que
// src/lib/oneclickCards.js / src/lib/mpCheckout.js / src/lib/mpReturn.js.
//
// [2026-09-14] REVERT (ADR-0007 Superseded, ver docs/adr/0007-...): Sima-apirest
// (P8) dejó de confiar en `codeComerce`/`codeStore`/`apiKey` enviados por el
// cliente — ahora los resuelve siempre server-side a partir de `maquina_id`
// (`server/utils/simaWebpay.ts`), sin fallback al comercio de integración
// genérico. Enviar estas credenciales en el body ya no tiene efecto en el
// backend, así que `filtrarCredencialesParaBody` (abajo) vuelve a devolver
// siempre `{}`. `resolverCredencialesTransbank` NO cambia: sigue resolviendo
// datos reales, porque `useOneClickComercio()` sigue dependiendo de ella para
// el filtro de tarjetas OneClick por comercio (ADR-0005), feature
// independiente de este revert.

import { resolverComercioEfectivo, normalizarComercio } from "@/lib/oneclickCards";

// Kill-switch histórico de ADR-0007 (mismo patrón que ONECLICK_FILTRO_ACTIVO /
// PAYMENT_LOCK_ACTIVO). Tras el revert del [2026-09-14] (ver cabecera del
// archivo) ya NO controla nada: `filtrarCredencialesParaBody` dejó de leerlo y
// siempre devuelve `{}`. Se conserva en `false` (nunca más `true`) sólo para
// no romper el import en `transbankCredenciales.test.js`/docs históricos que
// lo referencian — no reintroducir su lectura en `filtrarCredencialesParaBody`
// sin revisar por qué se revirtió (P8 en Sima-apirest: el backend ya no
// confía en credenciales de comercio enviadas por el cliente).
export const CREDENCIALES_EXPLICITAS_ACTIVAS = false;

export const MSG_SIN_CREDENCIALES_INSCRIPCION =
  "No pudimos identificar la tienda para asociar la tarjeta. Intenta nuevamente.";

// Clave nueva y aditiva de sessionStorage: nunca reemplaza ni toca
// mp_buyOrder/mp_storeId/mp_checkout_snapshot/checkout_claims.
export const TBK_CREDENCIALES_KEY = "tbk_intento_credenciales";
// 15 minutos, misma holgura que CHECKOUT_CLAIM_TTL_MS (paymentGuard.js): debe
// cubrir el ida-y-vuelta completo a Webpay.
export const TBK_CREDENCIALES_TTL_MS = 15 * 60 * 1000;

/**
 * resolverCredencialesTransbank({ metodo, appEnv, env, machine })
 * `metodo`: "oneclick" | "webpay".
 * `env`: objeto con { codeComerceDev, codeStoreDev, apiKeyDev, codeComerceWebpayDev }
 *   (inyectado, nunca leído de import.meta.env dentro de este módulo).
 * `machine`: objeto de useMachine().machine, puede ser null/undefined.
 *
 * Devuelve { codeComerce, apiKey } para "webpay" (sin codeStore: Webpay Plus
 * no lo usa) y { codeComerce, codeStore, apiKey } para "oneclick". Mismo
 * criterio dev/prod que resolverComercioEfectivo (development|testing -> dev).
 * Nunca lanza (optional chaining sobre `machine`).
 *
 * Siempre resuelve con datos reales — no depende de si
 * `filtrarCredencialesParaBody` termina incluyendo o no el resultado en un
 * body de Transbank. `useOneClickComercio()` depende de que esta función
 * devuelva siempre el comercio real para el filtro de tarjetas OneClick.
 */
export const resolverCredencialesTransbank = ({
  metodo,
  appEnv,
  env = {},
  machine,
} = {}) => {
  const esDev = appEnv === "development" || appEnv === "testing";

  if (metodo === "webpay") {
    const codeComerce = resolverComercioEfectivo({
      appEnv,
      codigoComercioDev: env.codeComerceWebpayDev,
      codigoComercioMachine: machine?.webpay_codigo_comercio,
    });
    const apiKey = normalizarComercio(
      esDev ? env.apiKeyDev : machine?.webpay_apikey,
    );
    return { codeComerce, apiKey };
  }

  // "oneclick" (por defecto)
  const codeComerce = resolverComercioEfectivo({
    appEnv,
    codigoComercioDev: env.codeComerceDev,
    codigoComercioMachine: machine?.oneclick_codigo_comercio,
  });
  const codeStore = normalizarComercio(
    esDev ? env.codeStoreDev : machine?.oneclick_codigo_tienda,
  );
  const apiKey = normalizarComercio(
    esDev ? env.apiKeyDev : machine?.oneclick_apikey,
  );
  return { codeComerce, codeStore, apiKey };
};

/**
 * credencialesCompletas(cred, metodo)
 * `true` sólo si todos los campos requeridos por `metodo` están presentes
 * (no vacíos tras normalizar). "webpay" no requiere codeStore.
 */
export const credencialesCompletas = (cred, metodo) => {
  if (!cred) return false;
  const codeComerce = normalizarComercio(cred.codeComerce);
  const apiKey = normalizarComercio(cred.apiKey);
  if (codeComerce === "" || apiKey === "") return false;
  if (metodo === "webpay") return true;
  return normalizarComercio(cred.codeStore) !== "";
};

/**
 * filtrarCredencialesParaBody(cred)
 * Único punto que decidía si `codeComerce`/`codeStore`/`apiKey` se incluían en
 * el body enviado a los 10 call sites de Transbank (ADR-0007). Revertido el
 * [2026-09-14]: Sima-apirest (P8) ya no confía en credenciales de comercio
 * enviadas por el cliente — las resuelve siempre server-side por
 * `maquina_id` — así que esta función siempre devuelve `{}`, sin importar lo
 * que traiga `cred`. Se conserva (en vez de eliminarla y quitar el spread de
 * cada call site) para que el punto de control siga existiendo si el backend
 * alguna vez vuelve a requerir credenciales explícitas del cliente. Los call
 * sites deben seguir usando esta función (nunca escribir
 * `codeComerce`/`codeStore`/`apiKey` a mano en el body).
 */
export const filtrarCredencialesParaBody = () => {
  return {};
};

// ---------------------------------------------------------------------------
// Snapshot por intento (sessionStorage) — para los caminos que cruzan un
// redirect (form Webpay -> checktransaction/reversetransaction, alta OneClick
// -> confirmación). Mismo patrón que guardarSnapshotMP/leerSnapshotMP en
// mpCheckout.js: todo el acceso a storage envuelto en try/catch, nunca lanza.
// ---------------------------------------------------------------------------

const leerAlmacen = () => {
  try {
    const raw = sessionStorage.getItem(TBK_CREDENCIALES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
};

const escribirAlmacen = (almacen) => {
  try {
    sessionStorage.setItem(TBK_CREDENCIALES_KEY, JSON.stringify(almacen));
  } catch {
    // sessionStorage no disponible/lleno: el snapshot es solo de apoyo; el
    // call site debe caer a la resolución en vivo si falta.
  }
};

/**
 * guardarCredencialesIntento(clave, cred)
 * Persiste `cred` bajo `clave` con TTL propio, sin tocar el resto del
 * almacén más allá de purgar entradas vencidas.
 */
export const guardarCredencialesIntento = (clave, cred) => {
  if (!clave || !cred) return;
  try {
    const almacen = leerAlmacen();
    const ahora = Date.now();
    const vigente = {};
    for (const [key, entry] of Object.entries(almacen)) {
      if (!entry || typeof entry.savedAt !== "number") continue;
      if (ahora - entry.savedAt > TBK_CREDENCIALES_TTL_MS) continue;
      vigente[key] = entry;
    }
    vigente[clave] = { ...cred, savedAt: ahora };
    escribirAlmacen(vigente);
  } catch {
    // no-op
  }
};

/**
 * leerCredencialesIntento(clave)
 * Devuelve la terna guardada bajo `clave` si existe y no expiró; `null` en
 * cualquier otro caso (clave ausente, TTL vencido, storage roto).
 */
export const leerCredencialesIntento = (clave) => {
  if (!clave) return null;
  try {
    const almacen = leerAlmacen();
    const entry = almacen[clave];
    if (!entry) return null;
    if (Date.now() - entry.savedAt > TBK_CREDENCIALES_TTL_MS) return null;
    // eslint-disable-next-line no-unused-vars
    const { savedAt, ...cred } = entry;
    return cred;
  } catch {
    return null;
  }
};

/**
 * limpiarCredencialesIntento(clave)
 * Elimina la entrada de `clave` tras usarla (éxito o rechazo definitivo).
 */
export const limpiarCredencialesIntento = (clave) => {
  if (!clave) return;
  try {
    const almacen = leerAlmacen();
    if (clave in almacen) {
      delete almacen[clave];
      escribirAlmacen(almacen);
    }
  } catch {
    // no-op
  }
};
